import { useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'

import Panel from '~/components/Panel'
import ThumbVideoZoomable from '~/components/ThumbVideoZoomable'
import ThumbZoomable from '~/components/ThumbZoomable'
import Form from '~/components/unform/Form'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import SponsoredBrand from '~/modules/retailMedia/components/CampaignForm/elements/SponsoredBrand'
import EditButton from '~/modules/retailMedia/components/EditButton'
import EditFormActions from '~/modules/retailMedia/components/EditFormActions'
import { useEditPanel } from '~/modules/retailMedia/hooks/useEditPanel'
import { patch } from '~/modules/retailMedia/store/industry/campaign/actions'

type AssetByDimension = {
  dimension: string
  assets: {
    type: 'image' | 'video'
    url: string
  }[]
}[]

const DetailsSponsoredBrands: React.FC<ViewCampaignProps> = ({ data }) => {
  const { t } = useAPPTranslation()
  const dispatch = useDispatch()

  const {
    isEditMode,
    setIsEditMode,
    loadingSave,
    canEdit,
    handleSuccessSubmit
  } = useEditPanel({ canEditCampaign: data?.canEditCampaign })

  const assetsGroupedByDimension: AssetByDimension =
    data?.settings?.assets?.reduce((acc: AssetByDimension, asset) => {
      const dimension = asset.dimension || 'default'
      const existingGroup = acc.find(group => group.dimension === dimension)

      if (existingGroup) {
        existingGroup.assets.push(asset)
      } else {
        acc.push({ dimension, assets: [asset] })
      }

      return acc
    }, [])

  // Handle upload media
  const rmid = data?.publisher?.id
  const formRef = useRef<FormHandles>(null)
  const handleAssetsIsValid = useCallback((): void => undefined, [])

  type SponsoredBrandFormData = {
    sponsored_brand: {
      headline?: string
      description?: string
      brand_name?: string
      brand_url?: string
      assets?: Array<{ dimension: string; type: string; url: string }>
    }
  }

  const handleSubmit = useCallback(
    (formData: SponsoredBrandFormData) => {
      const { sponsored_brand } = formData

      const settings = {
        headline: sponsored_brand?.headline,
        description: sponsored_brand?.description,
        brand_name: sponsored_brand?.brand_name,
        brand_url: sponsored_brand?.brand_url,
        assets: sponsored_brand?.assets || []
      }

      dispatch(
        patch({
          body: { settings },
          id: data.id,
          successFeedbackMsg: t('rm:actions.campaign.edit.successEdit'),
          onSuccess: handleSuccessSubmit,
          values: {
            patchKey: 'settings'
          }
        })
      )
    },
    [data.id, dispatch, t, handleSuccessSubmit]
  )

  return (
    <Panel
      title={t('rm:campaign.create.SponsoredBrand.elements.panelSettings')}
      template={isEditMode ? 'borderWarning' : 'default'}
      headerSideContent={
        canEdit && (
          <EditButton
            onClick={() => setIsEditMode(e => !e)}
            isActive={isEditMode}
          />
        )
      }
    >
      {/* Modo visualização */}
      {!isEditMode && (
        <>
          <p>
            <strong>
              {t('rm:campaign.create.SponsoredBrand.elements.title')}:
            </strong>{' '}
            {data.settings.headline || t('rm:indefinite')}
            <br />
            <strong>
              {t('rm:campaign.create.SponsoredBrand.elements.description')}:
            </strong>{' '}
            {data.settings.description || t('rm:indefinite')}
            <br />
            <strong>
              {t('rm:campaign.create.SponsoredBrand.elements.brandName')}:
            </strong>{' '}
            {data.settings.brand_name || t('rm:indefinite')}
          </p>

          {!!data?.settings?.brand_url && (
            <p>
              <strong>{t('rm:campaign.logo')}:</strong>
              <br />
              <img
                src={data.settings.brand_url}
                alt={data.settings.brand_name || ''}
              />
            </p>
          )}

          <section>
            <strong>{t('rm:media_other')}:</strong>

            {!assetsGroupedByDimension?.length && (
              <Alert variant="warning">
                {t('rm:campaign.noMediaRegistered')}
              </Alert>
            )}

            {assetsGroupedByDimension?.map(group => (
              <section key={group.dimension}>
                <code>{group.dimension}</code>
                <br />
                <div className="d-flex gap-2">
                  {group.assets.map(asset =>
                    asset.type === 'video' ? (
                      <ThumbVideoZoomable
                        key={asset.url}
                        url={asset.url}
                        title={t('c:actions.clickToZoom')}
                      />
                    ) : (
                      <ThumbZoomable
                        key={asset.url}
                        url={asset.url}
                        title={t('c:actions.clickToZoom')}
                      />
                    )
                  )}
                </div>
              </section>
            ))}
          </section>
        </>
      )}

      {/* Modo edição */}
      {isEditMode && (
        <Form
          onSubmit={handleSubmit}
          initialData={{ sponsored_brand: data.settings }}
          ref={formRef}
        >
          <SponsoredBrand
            formRef={formRef}
            initialData={{ publisher_id: rmid, settings: data.settings }}
            handleIsValid={handleAssetsIsValid}
            type={data.adType === 'sponsored_brand_video' ? 'video' : 'image'}
          />
          <EditFormActions
            loadingSave={loadingSave}
            onCancel={() => setIsEditMode(false)}
          />
        </Form>
      )}
    </Panel>
  )
}

export default DetailsSponsoredBrands
